import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Alert, Button } from "@sortera/react-components";
import type { AgentModelDto, MeDto, ProjectDetailsDto, SuggestionDto } from "../../types/index.ts";
import { createConversation, fetchSuggestions } from "../../integrations/api.ts";
import { ApiError } from "../../integrations/http.ts";
import { SortingScene } from "../../ui/sorting-scene/index.ts";
import { Spinner } from "../../ui/spinner/index.ts";
import { LimitNotice, type LimitLock } from "../limit-lock/index.ts";
import { Composer } from "../composer/index.ts";
import { ModelPicker, useModelChoice } from "../model-picker/index.ts";
import { UsageMeter } from "../usage-meter/index.ts";
import { AssistantMessage, UserMessage } from "../message/index.ts";
import type { PreviewEvent } from "../preview-panel/index.ts";
import { Welcome } from "../welcome/index.ts";
import { useConversation, type SaveEvent } from "./use-conversation.ts";
import styles from "./chat-view.module.css";

export interface ChatViewProps {
  project: ProjectDetailsDto;
  /** For the month's usage, shown just above the box. */
  me: MeDto | null;
  /** The models the person may choose between, from the server's config. */
  models: AgentModelDto[];
  /** While locked, nothing can be started or sent, and the person is told why. */
  lock: LimitLock;
  onLimitReached: () => void;
  conversationId: string | null;
  onOpenConversation: (id: string) => void;
  onTurnFinished: () => void;
  onPreviewEvent?: (event: PreviewEvent) => void;
  onSaveEvent?: (event: SaveEvent) => void;
  /** At the right end of the row above the box, beside the month's usage: "Lägg upp i testmiljön". */
  action?: ReactNode;
}

export function ChatView({ project, me, models, lock, onLimitReached, conversationId, onOpenConversation, onTurnFinished, onPreviewEvent, onSaveEvent, action }: ChatViewProps) {
  const [model, chooseModel] = useModelChoice(models);
  const { conversation, live, error, loading, send, stop, queueFirstMessage, dismissError } = useConversation(
    conversationId,
    onTurnFinished,
    onPreviewEvent,
    model,
    onLimitReached,
    onSaveEvent,
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // First-message ideas for this project: undefined while on their way, null when there are none.
  const [suggestions, setSuggestions] = useState<SuggestionDto[] | null | undefined>(undefined);
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  // Follow the answer as it is written — unless the person scrolled up to read something.
  const hasContent = Boolean(conversation?.messages.length) || Boolean(live);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && pinned.current && hasContent) el.scrollTop = el.scrollHeight;
  });
  useEffect(() => {
    if (!project.summary) return;
    let current = true;
    setSuggestions(undefined);
    fetchSuggestions(project.repo)
      .catch(() => null)
      .then((found) => current && setSuggestions(found));
    return () => {
      current = false;
    };
  }, [project.repo, project.summary]);

  useEffect(() => {
    pinned.current = true;
  }, [conversationId]);

  const onScroll = () => {
    const el = scroller.current;
    if (el) pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleSend = async (text: string) => {
    pinned.current = true;
    if (conversationId) {
      void send(text);
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createConversation(project.repo);
      queueFirstMessage(created.id, text);
      onOpenConversation(created.id);
    } catch (e) {
      setCreateError(e instanceof ApiError ? e.message : "Konversationen kunde inte skapas.");
    } finally {
      setCreating(false);
    }
  };

  const messages = conversation?.messages ?? [];
  // Following a turn after a reload: the stored placeholder for it (empty, unfinished) is
  // replaced by the live view. A normal send never has one — its rows are read after it ends.
  const last = messages.at(-1);
  const placeholder = live && last?.role === "assistant" && !last.text && !last.usage && !last.stopReason;
  const shown = placeholder ? messages.slice(0, -1) : messages;
  const empty = !conversationId && !live;
  const running = Boolean(live) || creating;

  return (
    <div className={styles.view} data-empty={empty}>
      {/* Before the work starts: people sorting, in the lower right corner. Gone with the first message. */}
      {empty && <SortingScene className={styles.scene} />}
      <div className={styles.scroller} ref={scroller} onScroll={onScroll}>
        <div className={styles.column}>
          {empty && (
            <Welcome
              projectName={project.name}
              stage={project.stage}
              summary={project.summary}
              suggestions={project.summary ? suggestions : null}
              disabled={creating || lock.locked}
              onPick={(text) => void handleSend(text)}
            />
          )}

          {loading && !conversation && (
            <p className={styles.loading}>
              <Spinner /> Hämtar konversationen …
            </p>
          )}

          <ol className={styles.messages}>
            {shown.map((m) => (
              <li key={m.id}>
                {m.role === "user" ? (
                  <UserMessage text={m.text} />
                ) : (
                  <AssistantMessage
                    text={m.text}
                    steps={m.steps}
                    running={false}
                    stopReason={m.stopReason}
                    usage={m.usage}
                    sekPerUsd={me?.sekPerUsd}
                  />
                )}
              </li>
            ))}
            {live && (
              <>
                {live.question && (
                  <li>
                    <UserMessage text={live.question} />
                  </li>
                )}
                <li>
                  <AssistantMessage
                    text={live.text}
                    steps={live.steps}
                    running={!live.saved}
                    status={live.status}
                    errors={live.errors}
                    stopReason={live.stopReason}
                  />
                </li>
              </>
            )}
          </ol>
        </div>
      </div>

      <div className={styles.dock}>
        <div className={styles.column}>
          {(error || createError) && (
            <Alert
              variant="critical"
              className={styles.error}
              action={
                <Button variant="text" size="small" color="secondary" onClick={() => (dismissError(), setCreateError(null))}>
                  Stäng
                </Button>
              }
            >
              {error ?? createError}
            </Alert>
          )}
          <LimitNotice lock={lock} className={styles.error} />
          {(me || action) && (
            <div className={styles.above}>
              {me && <UsageMeter me={me} />}
              {action}
            </div>
          )}
          <Composer
            running={running}
            disabled={creating || lock.locked}
            placeholder={empty ? "Beskriv vad du vill bygga eller ändra …" : "Svara eller be om nästa sak …"}
            projectName={project.name}
            tools={<ModelPicker models={models} model={model} onChange={chooseModel} />}
            onSend={(text) => void handleSend(text)}
            onStop={() => void stop()}
          />
          <p className={styles.footnote}>
            Claude kan ha fel. Det som läggs upp hamnar i testmiljön för {project.name} — titta på det innan någon annan gör det.
          </p>
        </div>
      </div>
    </div>
  );
}
